import { useState } from 'react';
import { useDeliveryLog, useDeliveryMutations } from '../../hooks/useDelivery';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { SendQrModal } from './SendQrModal';
import type { DeliveryEntry, DeliveryStatus, DeliverySource } from '../../types/delivery';

const STATUS_LABELS: Record<DeliveryStatus, string> = {
  accepted: 'Отправлено',
  failed: 'Ошибка',
  skipped: 'Пропущено',
};

const STATUS_CLASSES: Record<DeliveryStatus, string> = {
  accepted: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-red-50 text-red-700',
  skipped: 'bg-slate-100 text-slate-600',
};

const SOURCE_LABELS: Record<DeliverySource, string> = {
  import: 'Импорт',
  batch: 'Рассылка',
  resend: 'Повтор',
};

const PAGE_SIZE = 20;

function formatDate(value: string): string {
  if (!value) return '';
  const normalized = value.includes('T') ? value : value.replace(' ', 'T') + 'Z';
  const d = new Date(normalized);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function DeliveryPanel() {
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sendTarget, setSendTarget] = useState<DeliveryEntry | null>(null);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [retryResult, setRetryResult] = useState('');

  const { data, isLoading, isError, refetch, isFetching } = useDeliveryLog({ status, source, search, page, limit: PAGE_SIZE });
  const { retryFailed } = useDeliveryMutations();

  const entries = data?.entries ?? [];
  const summary = data?.summary;
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const failedCount = summary?.failed ?? 0;

  const selectClass = 'rounded-lg border border-ct-border bg-ct-surface px-3 py-2 text-sm text-ct-text shadow-ct-sm focus:outline-none focus:ring-2 focus:ring-ct-primary/30';

  function changeFilter(setter: (v: string) => void, value: string) {
    setter(value);
    setPage(1);
  }

  async function handleRetryFailed() {
    setConfirmBulk(false);
    setRetryResult('');
    try {
      const res = await retryFailed.mutateAsync();
      setRetryResult(`Обработано: ${res.retried} · успешно: ${res.succeeded} · с ошибкой: ${res.failed}`);
    } catch (err: any) {
      setRetryResult(err.message || 'Ошибка массовой рассылки');
    }
  }

  return (
    <div className="mt-8 rounded-xl bg-ct-surface border border-ct-border shadow-ct-sm p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-extrabold text-ct-text">Доставка QR-писем</h2>
        <div className="flex items-center gap-2">
          {isFetching && <span className="text-xs text-ct-text-muted">Обновление…</span>}
          <button
            onClick={() => refetch()}
            className="rounded-lg border border-ct-border bg-ct-surface px-3 py-1.5 text-xs font-semibold text-ct-text-muted shadow-ct-sm transition-all duration-200 hover:bg-indigo-50 hover:text-ct-primary active:scale-[0.98]"
          >
            Обновить
          </button>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold">
        <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">Всего: {summary?.total ?? 0}</span>
        <span className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-700">Отправлено: {summary?.accepted ?? 0}</span>
        <span className="rounded-full bg-red-50 px-3 py-1 text-red-700">Ошибка: {summary?.failed ?? 0}</span>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-600">Пропущено: {summary?.skipped ?? 0}</span>
      </div>

      {failedCount > 0 && (
        <div className="mt-3">
          <button
            onClick={() => setConfirmBulk(true)}
            disabled={retryFailed.isPending}
            className="rounded-full bg-gradient-to-r from-red-500 to-red-600 px-5 py-2 text-sm font-bold text-white shadow-ct-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-ct-md active:scale-[0.98] disabled:opacity-50"
          >
            {retryFailed.isPending ? 'Переотправка…' : `Переотправить неудачные (${failedCount})`}
          </button>
        </div>
      )}

      {retryResult && (
        <div className="mt-3 rounded-lg border border-ct-border bg-indigo-50/50 px-3 py-2 text-sm text-ct-text">
          {retryResult}
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <select value={status} onChange={(e) => changeFilter(setStatus, e.target.value)} className={selectClass}>
          <option value="">Все статусы</option>
          <option value="accepted">Отправлено</option>
          <option value="failed">Ошибка</option>
          <option value="skipped">Пропущено</option>
        </select>
        <select value={source} onChange={(e) => changeFilter(setSource, e.target.value)} className={selectClass}>
          <option value="">Все источники</option>
          <option value="import">Импорт</option>
          <option value="batch">Рассылка</option>
          <option value="resend">Повтор</option>
        </select>
        <input
          type="text"
          value={search}
          onChange={(e) => changeFilter(setSearch, e.target.value)}
          placeholder="Поиск по ФИО, компании, email…"
          className={`${selectClass} min-w-[220px] flex-1`}
        />
      </div>

      {isError ? (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">
          Ошибка загрузки данных о доставке
        </div>
      ) : isLoading && entries.length === 0 ? (
        <div className="mt-3 text-sm text-ct-text-muted">Загрузка…</div>
      ) : entries.length === 0 ? (
        <div className="mt-3 text-sm text-ct-text-muted">Записей нет</div>
      ) : (
        <div className="mt-3 max-h-96 overflow-auto rounded-xl border border-ct-border">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0">
              <tr className="bg-gradient-to-r from-ct-primary to-ct-secondary text-ct-on-primary">
                <th className="px-3 py-2.5 font-semibold text-ct-on-primary/90">Участник</th>
                <th className="px-3 py-2.5 font-semibold text-ct-on-primary/90">Email</th>
                <th className="px-3 py-2.5 font-semibold text-ct-on-primary/90">Статус</th>
                <th className="px-3 py-2.5 font-semibold text-ct-on-primary/90">Источник</th>
                <th className="px-3 py-2.5 font-semibold text-ct-on-primary/90">Попытки</th>
                <th className="px-3 py-2.5 font-semibold text-ct-on-primary/90">Обновлено</th>
                <th className="px-3 py-2.5 font-semibold text-ct-on-primary/90 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} className="border-t border-ct-border hover:bg-indigo-50/40 transition-colors duration-150">
                  <td className="px-3 py-2">
                    <div className="text-ct-text">{entry.l_name} {entry.f_name}</div>
                    {entry.company && <div className="text-xs text-ct-text-muted">{entry.company}</div>}
                  </td>
                  <td className="px-3 py-2 text-ct-text-muted">
                    <div>{entry.email || '—'}</div>
                    {entry.sent_to && entry.sent_to !== entry.email && (
                      <div className="text-xs text-ct-text-muted">отправлено на: {entry.sent_to}</div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_CLASSES[entry.status]}`}>
                      {STATUS_LABELS[entry.status]}
                    </span>
                    {entry.status === 'failed' && entry.last_error && (
                      <div className="mt-1 text-xs text-red-600" title={entry.last_error}>{entry.last_error}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-ct-text-muted">{SOURCE_LABELS[entry.last_source]}</td>
                  <td className="px-3 py-2 text-ct-text-muted">{entry.attempts}</td>
                  <td className="px-3 py-2 text-ct-text-muted">{formatDate(entry.updated_at)}</td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => setSendTarget(entry)}
                      className="rounded-lg border border-ct-border bg-ct-surface px-3 py-1.5 text-xs font-semibold text-ct-primary shadow-ct-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-ct-md active:scale-[0.98]"
                    >
                      Отправить QR
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-2 flex items-center justify-between text-sm text-ct-text-muted">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="rounded-lg border border-ct-border bg-ct-surface px-4 py-1.5 text-sm font-semibold text-ct-text-muted shadow-ct-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-ct-md hover:text-ct-primary active:scale-[0.98] disabled:opacity-50"
          >
            Назад
          </button>
          <span>Стр. {page} из {totalPages} · всего {total}</span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="rounded-lg border border-ct-border bg-ct-surface px-4 py-1.5 text-sm font-semibold text-ct-text-muted shadow-ct-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-ct-md hover:text-ct-primary active:scale-[0.98] disabled:opacity-50"
          >
            Вперёд
          </button>
        </div>
      )}

      <SendQrModal
        open={sendTarget !== null}
        guestId={sendTarget?.guest_id ?? null}
        participantName={sendTarget ? `${sendTarget.l_name} ${sendTarget.f_name}` : ''}
        storedEmail={sendTarget?.email ?? ''}
        initialEmail={sendTarget?.sent_to || sendTarget?.email || ''}
        onClose={() => setSendTarget(null)}
        onSent={(message) => setRetryResult(message)}
      />

      <ConfirmDialog
        open={confirmBulk}
        title="Переотправить неудачные"
        message={`Повторно отправить QR-коды для всех неудачных писем (${failedCount})?`}
        confirmLabel="Переотправить все"
        cancelLabel="Отмена"
        onConfirm={handleRetryFailed}
        onCancel={() => setConfirmBulk(false)}
      />
    </div>
  );
}
